/**
 * Persisted bridge for exp_onboarding_plan_cc_capture_v2_20676 (#20676)
 *
 * Stores the plan id selected on the PlanSelection20676 page, and the
 * personalized/generic title it resolved, so PaymentCapture20676 (a
 * separate Questionnaire page with no shared component state) can pick
 * them up.
 *
 * Persisted to localStorage, not just kept in memory: `questionnairePosition`
 * already survives a page reload, so a reload on PaymentCapture20676 would
 * resume the user right back there -- but with an in-memory-only value, it
 * would have no record of the plan they picked, and would silently default
 * to the annual plan regardless of what was actually selected.
 *
 * Cleared once onboarding finishes (see Onboarding.vue's redirect logic) so
 * a stale value can't leak into a later onboarding session.
 */

import { storage } from '@/helpers/storage'
import { StorageKeys } from '@/helpers/storage.keys'
import type { Raw } from 'vue'
import type { BillingView } from '@/models/billing'
import type { InterestBook } from '@/helpers/billingSourceBook'

/**
 * Save the selected plan id. Pass null to remove it.
 */
export function setSelectedPlanId20676(planId: string | null): void {
  if (planId === null) {
    storage.removeItem(StorageKeys.selectedPlanId20676)
  } else {
    storage.setItem(StorageKeys.selectedPlanId20676, planId)
  }
}

/**
 * Get the saved plan id, or null if none is saved.
 */
export function getSelectedPlanId20676(): string | null {
  return storage.getItem(StorageKeys.selectedPlanId20676)
}

/**
 * Save the resolved title. Pass null to remove it.
 */
export function setResolvedTitle20676(title: string | null): void {
  if (title === null) {
    storage.removeItem(StorageKeys.resolvedTitle20676)
  } else {
    storage.setItem(StorageKeys.resolvedTitle20676, title)
  }
}

/**
 * Get the saved resolved title, or null if none is saved.
 */
export function getResolvedTitle20676(): string | null {
  return storage.getItem(StorageKeys.resolvedTitle20676)
}

/**
 * Save the interest book. Pass null to remove it.
 */
export function setInterestBook20676(book: InterestBook | null): void {
  if (book === null) {
    storage.removeItem(StorageKeys.interestBook20676)
  } else {
    storage.setItem(StorageKeys.interestBook20676, JSON.stringify(book))
  }
}

/**
 * Get the saved interest book, or null if none is saved.
 */
export function getInterestBook20676(): InterestBook | null {
  const data = storage.getItem(StorageKeys.interestBook20676)
  return data ? (JSON.parse(data) as InterestBook) : null
}

/**
 * Remove the selected plan id, resolved title, and interest book. Call this
 * once onboarding finishes, right before redirecting the user into the app.
 */
export function clearPlanCardCapture20676(): void {
  storage.removeItem(StorageKeys.selectedPlanId20676)
  storage.removeItem(StorageKeys.resolvedTitle20676)
  storage.removeItem(StorageKeys.interestBook20676)
}

// Nothing on PlanSelection20676 or PaymentCapture20676 can change billing
// state (no subscribe happens until PaymentCapture20676's own submit), so
// the billing fetched for the plan cards is still accurate one page later,
// cache it instead of fetching (and reconstructing a BillingView) again.
let cachedBilling20676: Raw<BillingView> | null = null

/**
 * Save the billing view in memory, so the next page does not have to fetch
 * it again.
 */
export function setCachedBilling20676(billing: Raw<BillingView> | null): void {
  cachedBilling20676 = billing
}

/**
 * Get the cached billing view, or null if none is saved.
 */
export function getCachedBilling20676(): Raw<BillingView> | null {
  return cachedBilling20676
}
